import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  Trophy, Lightning, Megaphone, CalendarBlank, Users, ArrowRight,
  GameController, Crown, UserCircle, Target, Medal, CalendarCheck,
  ChartLineUp, Fire, Ranking, Clock, Warning, CurrencyCircleDollar
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SmartAlertsCard from "@/components/dashboard/SmartAlertsCard";
import { toLocalDateString } from "@/lib/localDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';

// ==================== MY PLAYER CARD ====================
function MyPlayerCard({ player, analytics, achievements, recentForm }) {
  const winRate = player.matches_played > 0
    ? ((player.matches_won / player.matches_played) * 100).toFixed(1)
    : 0;

  return (
    <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent" data-testid="my-player-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-4 mb-4">
          <Avatar className="h-14 w-14 border-2 border-primary/30">
            <AvatarImage src={player.photo_url} />
            <AvatarFallback className="text-lg">{getInitials(player.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-bold truncate">{player.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className={player.tier === 'premier' ? 'tier-premier' : 'tier-challenger'}>
                {player.tier === 'premier' ? 'Premier' : 'Challenger'}
              </Badge>
              <span className="text-xs text-muted-foreground">Rank #{player.ranking}</span>
            </div>
          </div>
          <Link to={`/players/${player.id}`}>
            <Button variant="outline" size="sm" className="border-border text-xs">Profile</Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-2.5 rounded-lg bg-card border border-border text-center">
            <p className="text-lg font-heading font-bold text-primary">{player.matches_played}</p>
            <p className="text-[10px] text-muted-foreground">Played</p>
          </div>
          <div className="p-2.5 rounded-lg bg-card border border-border text-center">
            <p className="text-lg font-heading font-bold text-primary">{player.matches_won}</p>
            <p className="text-[10px] text-muted-foreground">Won</p>
          </div>
          <div className="p-2.5 rounded-lg bg-card border border-border text-center">
            <p className="text-lg font-heading font-bold">{winRate}%</p>
            <p className="text-[10px] text-muted-foreground">Win Rate</p>
          </div>
          <div className="p-2.5 rounded-lg bg-card border border-border text-center">
            <p className="text-lg font-heading font-bold text-secondary">{player.match_points_total || 0}</p>
            <p className="text-[10px] text-muted-foreground">Match Pts</p>
          </div>
        </div>

        {/* Last 5 Results */}
        {recentForm && recentForm.length > 0 && (
          <div className="mt-3">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Last 5</p>
            <div className="flex gap-1">
              {recentForm.map((r, i) => (
                <span key={`form-${i}-${r}`} className={`w-7 h-7 rounded flex items-center justify-center text-xs font-bold ${
                  r === 'W' ? 'bg-primary/15 text-primary' :
                  r === 'L' ? 'bg-destructive/15 text-destructive' :
                  'bg-muted text-muted-foreground'
                }`}>{r}</span>
              ))}
            </div>
          </div>
        )}

        {/* Achievements */}
        {achievements && achievements.length > 0 && (
          <div className="mt-3">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Achievements</p>
            <div className="flex flex-wrap gap-1.5">
              {achievements.slice(0, 5).map((a, i) => (
                <Badge key={a.name || `ach-${i}`} variant="outline" className="text-[10px] border-secondary/30 text-secondary">
                  {a.name}
                </Badge>
              ))}
              {achievements.length > 5 && (
                <Badge variant="outline" className="text-[10px]">+{achievements.length - 5}</Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== ADMIN OVERVIEW ====================
function AdminOverview({ stats, disputes }) {
  const pendingMatches = (stats?.total_matches || 0) - (stats?.live_matches || 0);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-overview">
      <Card className="bg-card border-border">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="w-4 h-4 text-primary" weight="fill" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold">{stats?.total_players || 0}</p>
              <p className="text-[10px] text-muted-foreground">Players</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-card border-border">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-secondary/10">
              <Trophy className="w-4 h-4 text-secondary" weight="fill" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold">{stats?.total_tournaments || 0}</p>
              <p className="text-[10px] text-muted-foreground">Tournaments</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-card border-border">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-chart-3/10">
              <Target className="w-4 h-4 text-chart-3" weight="fill" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold">{stats?.total_matches || 0}</p>
              <p className="text-[10px] text-muted-foreground">Matches</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-card border-border">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-chart-4/10">
              <Lightning className="w-4 h-4 text-chart-4" weight="fill" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold">{stats?.live_matches || 0}</p>
              <p className="text-[10px] text-muted-foreground">Live Now</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function HomePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const [stats, setStats] = useState(null);
  const [topRankings, setTopRankings] = useState([]);
  const [leaderboards, setLeaderboards] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [myMatches, setMyMatches] = useState([]);
  const [myPlayer, setMyPlayer] = useState(null);
  const [myAchievements, setMyAchievements] = useState([]);
  const [myFinance, setMyFinance] = useState(null);
  const [currency, setCurrency] = useState({ currency: "TZS", currency_symbol: "TSh" });
  const [calendarMatches, setCalendarMatches] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("all");

  const isAdmin = user?.is_admin;
  const claimedPlayerId = user?.claimed_player_id;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const requests = [
          axios.get(`${API}/public/home`).catch(() => ({ data: {} })),
          axios.get(`${API}/stats/overview`).catch(() => ({ data: {} })),
          axios.get(`${API}/rankings?limit=5`).catch(() => ({ data: [] })),
          axios.get(`${API}/stats/leaderboards`).catch(() => ({ data: {} })),
          axios.get(`${API}/matches/recent?limit=5`).catch(() => ({ data: [] })),
        ];
        const [homeRes, statsRes, rankRes, lbRes, recentRes] = await Promise.all(requests);
        setHomeData(homeRes.data);
        setStats(statsRes.data);
        setTopRankings(rankRes.data);
        setLeaderboards(lbRes.data);
        setRecentMatches(recentRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  // Fetch player-specific data
  useEffect(() => {
    if (!claimedPlayerId) return;
    const fetchPlayerData = async () => {
      try {
        const [playerRes, achieveRes, matchesRes, financeRes, currencyRes] = await Promise.all([
          axios.get(`${API}/players/${claimedPlayerId}`).catch(() => ({ data: null })),
          axios.get(`${API}/players/${claimedPlayerId}/achievements`).catch(() => ({ data: { achievements: [] } })),
          axios.get(`${API}/matches/my-schedule`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/finance/my-summary`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/settings/finance`).catch(() => ({ data: { currency: "TZS", currency_symbol: "TSh" } })),
        ]);
        setMyPlayer(playerRes.data);
        setMyAchievements(achieveRes.data?.achievements || []);
        setMyMatches(matchesRes.data || []);
        setMyFinance(financeRes.data);
        setCurrency(currencyRes.data);
      } catch { /* ignore */ }
    };
    fetchPlayerData();
  }, [claimedPlayerId]);

  // Calendar
  const fetchCalendar = useCallback(async () => {
    try {
      const month = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}`;
      const res = await axios.get(`${API}/matches/calendar?month=${month}`);
      setCalendarMatches(res.data || []);
    } catch { setCalendarMatches([]); }
  }, [selectedDate]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const matchDates = useMemo(() => {
    const src = calendarView === "my" ? myMatches : calendarMatches;
    return src.filter(m => m.scheduled_date).map(m => new Date(m.scheduled_date + "T00:00:00"));
  }, [calendarMatches, myMatches, calendarView]);

  const selectedDateStr = selectedDate ? toLocalDateString(selectedDate) : undefined;
  const matchesOnDate = useMemo(() => {
    const src = calendarView === "my" ? myMatches : calendarMatches;
    return src.filter(m => m.scheduled_date === selectedDateStr);
  }, [calendarMatches, myMatches, selectedDateStr, calendarView]);

  const { ongoing_tournaments = [], upcoming_tournaments = [], announcements = [], live_matches = [], recent_results = [] } = homeData || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Hero — compact for logged-in users (they care about insights, not pitch). */}
      {user ? (
        <div className="flex items-center justify-between flex-wrap gap-3 py-2" data-testid="home-hero-compact">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Annadil Burhani DSM" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="font-heading text-lg sm:text-xl font-bold" data-testid="home-title">
                Welcome back{myPlayer?.name ? `, ${myPlayer.name.split(' ')[0]}` : ""}
              </h1>
              <p className="text-xs text-muted-foreground">Your dashboard</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/rankings">
              <Button size="sm" variant="outline"><Trophy className="w-4 h-4 mr-1.5" weight="fill" />Rankings</Button>
            </Link>
            <Link to="/matches/live">
              <Button size="sm" variant="outline"><Lightning className="w-4 h-4 mr-1.5" weight="fill" />Match Centre</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 sm:py-8" data-testid="home-hero-public">
          <img src="/logo.png" alt="Annadil Burhani DSM" className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 object-contain" />
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-2" data-testid="home-title">
            Annadil Burhani DSM Snooker
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            Official ranking system, tournaments & live scoring
          </p>
          <div className="mt-4 flex justify-center gap-3">
            <Link to="/rankings">
              <Button size="sm" data-testid="view-rankings-btn"><Trophy className="w-4 h-4 mr-1.5" weight="fill" />Rankings</Button>
            </Link>
            <Link to="/matches/live">
              <Button variant="outline" size="sm"><Lightning className="w-4 h-4 mr-1.5" weight="fill" />Match Centre</Button>
            </Link>
          </div>
        </div>
      )}

      {/* Admin Overview */}
      {isAdmin && stats && <AdminOverview stats={stats} />}

      {/* Logged-out CTA — nudge visitors to sign in for personalised content */}
      {!user && (
        <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-secondary/5" data-testid="signin-cta">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/15">
                <Lightning className="w-5 h-5 text-primary" weight="fill" />
              </div>
              <div>
                <p className="font-heading font-semibold text-sm">See your personalised dashboard</p>
                <p className="text-xs text-muted-foreground">
                  Sign in to view your stats, upcoming matches, finances & smart alerts.
                </p>
              </div>
            </div>
            <Button size="sm" onClick={() => (window.location.href = "/login")} data-testid="cta-sign-in">
              Sign In
            </Button>
          </CardContent>
        </Card>
      )}

      {/* My Player Card (signed-in with claimed profile) */}
      {myPlayer && (
        <MyPlayerCard
          player={myPlayer}
          achievements={myAchievements}
          recentForm={myPlayer.recent_form}
        />
      )}

      {/* My Upcoming Matches */}
      {myMatches.length > 0 && (
        <Card className="border-primary/30 bg-primary/5" data-testid="my-matches-card">
          <CardContent className="p-4">
            <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-primary" weight="fill" />
              Your Upcoming Matches
            </h3>
            <div className="space-y-2">
              {myMatches.slice(0, 3).map(m => (
                <div key={m.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-card border border-border">
                  <span className="flex-1 text-right text-sm font-medium truncate">{m.player1_name}</span>
                  <span className="text-xs text-muted-foreground px-1">vs</span>
                  <span className="flex-1 text-sm font-medium truncate">{m.player2_name}</span>
                  <span className="text-[10px] text-primary whitespace-nowrap">
                    {m.scheduled_date || 'TBD'} {m.scheduled_time ? `${m.scheduled_time}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart Alerts — rule-based personalised messaging */}
      {user && <SmartAlertsCard />}

      {/* My Finance Summary */}
      {myFinance && myFinance.has_claimed_profile && (myFinance.fees.length > 0 || myFinance.payouts.length > 0) && (
        <MyFinanceCard data={myFinance} symbol={currency.currency_symbol || currency.currency} />
      )}

      {/* Live Matches Banner */}
      {live_matches.length > 0 && (
        <Card className="border-red-500/40 bg-red-500/5" data-testid="live-matches-section">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" /></span>
              Live Now
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {live_matches.map(m => (
                <Link key={m.id} to="/matches/live" className="block">
                  <div className="flex items-center justify-between p-2.5 rounded-lg bg-card border border-border hover:border-red-500/30 transition-colors">
                    <span className="text-sm font-medium truncate flex-1 text-right">{m.player1_name}</span>
                    <span className="font-heading font-bold text-primary px-2">{m.player1_score || 0} - {m.player2_score || 0}</span>
                    <span className="text-sm font-medium truncate flex-1">{m.player2_name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Announcements */}
      {announcements.length > 0 && (
        <div className="space-y-2" data-testid="announcements-section">
          {announcements.map(a => (
            <Card key={a.id} className="border-secondary/30 bg-secondary/5">
              <CardContent className="p-3 flex items-start gap-2.5">
                <Megaphone className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" weight="fill" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.message}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Grid: Rankings + Tournaments | Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Tournaments Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card data-testid="ongoing-tournaments-section">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-secondary" weight="fill" />Ongoing
                  </CardTitle>
                  <Link to="/tournaments"><Button variant="ghost" size="sm" className="text-primary text-xs h-7">View All <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {ongoing_tournaments.length > 0 ? ongoing_tournaments.map(t => (
                  <Link key={t.id} to={`/tournaments/${t.slug || t.id}`} className="block">
                    <div className="p-2.5 rounded-lg border border-border hover:border-primary/30 transition-colors">
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {t.tournament_format?.replace('_', ' ')} &middot; {t.player_ids?.length || t.registered_players?.length || 0} players
                      </p>
                    </div>
                  </Link>
                )) : <p className="text-xs text-muted-foreground text-center py-4">No ongoing tournaments</p>}
              </CardContent>
            </Card>

            <Card data-testid="upcoming-tournaments-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarBlank className="w-4 h-4 text-chart-3" weight="fill" />Upcoming
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {upcoming_tournaments.length > 0 ? upcoming_tournaments.map(t => (
                  <Link key={t.id} to={`/tournaments/${t.slug || t.id}`} className="block">
                    <div className="p-2.5 rounded-lg border border-border hover:border-primary/30 transition-colors">
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{t.date}</p>
                    </div>
                  </Link>
                )) : <p className="text-xs text-muted-foreground text-center py-4">No upcoming tournaments</p>}
              </CardContent>
            </Card>
          </div>

          {/* Top Rankings */}
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Crown className="w-4 h-4 text-secondary" weight="fill" />Top Rankings
              </CardTitle>
              <Link to="/rankings"><Button variant="ghost" size="sm" className="text-xs h-7" data-testid="view-all-rankings">View All <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {topRankings.map((p, i) => (
                  <Link key={p.id} to={`/players/${p.id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid={`ranking-row-${i + 1}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'rank-1' : i === 1 ? 'rank-2' : i === 2 ? 'rank-3' : 'bg-muted text-muted-foreground'
                    }`}>{i + 1}</div>
                    <Avatar className="h-8 w-8 border border-border">
                      <AvatarImage src={p.photo_url} />
                      <AvatarFallback className="text-xs">{getInitials(p.name)}</AvatarFallback>
                    </Avatar>
                    <span className="flex-1 text-sm font-medium truncate">{p.name}</span>
                    <Badge className={`text-[10px] ${p.tier === 'premier' ? 'tier-premier' : 'tier-challenger'}`}>
                      {p.tier === 'premier' ? 'Premier' : 'Challenger'}
                    </Badge>
                    <span className="font-heading font-bold text-primary text-sm">{p.total_points}</span>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Leaderboards */}
          <div>
            <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
              <Medal className="w-5 h-5 text-secondary" weight="fill" />Leaderboards
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { title: "Centuries", data: leaderboards?.most_centuries, field: "century_breaks", color: "text-primary" },
                { title: "High Breaks", data: leaderboards?.highest_breaks, field: "highest_break", color: "text-secondary" },
                { title: "Most Wins", data: leaderboards?.most_wins, field: "matches_won", color: "text-chart-3" },
                { title: "Titles", data: leaderboards?.most_tournament_wins, field: "tournament_wins", color: "text-chart-4" },
              ].map(lb => (
                <Card key={lb.title} className="bg-card border-border">
                  <CardHeader className="pb-1 px-3 pt-3">
                    <CardTitle className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{lb.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <div className="space-y-1.5">
                      {lb.data?.slice(0, 3).map((p, i) => (
                        <Link key={p.id} to={`/players/${p.id}`} className="flex items-center gap-1.5 hover:bg-white/5 rounded p-1">
                          <span className="text-[10px] text-muted-foreground w-3">{i + 1}</span>
                          <Avatar className="h-5 w-5"><AvatarImage src={p.photo_url} /><AvatarFallback className="text-[8px]">{getInitials(p.name)}</AvatarFallback></Avatar>
                          <span className="text-xs flex-1 truncate">{p.name}</span>
                          <span className={`text-xs font-bold ${lb.color}`}>{p[lb.field]}</span>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Recent Results */}
          {(recentMatches.length > 0 || recent_results.length > 0) && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <GameController className="w-4 h-4 text-primary" weight="fill" />Recent Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {(recentMatches.length > 0 ? recentMatches : recent_results).map(m => (
                  <div key={m.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/20">
                    <span className={`text-sm flex-1 text-right truncate ${m.winner_id === m.player1_id ? 'font-bold text-primary' : 'text-muted-foreground'}`}>{m.player1_name}</span>
                    <span className="font-heading font-bold text-sm px-2">{m.player1_score} - {m.player2_score}</span>
                    <span className={`text-sm flex-1 truncate ${m.winner_id === m.player2_id ? 'font-bold text-primary' : 'text-muted-foreground'}`}>{m.player2_name}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar: Calendar + Quick Links */}
        <div className="space-y-4">
          <Card data-testid="match-calendar-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarBlank className="w-4 h-4 text-primary" weight="fill" />Match Calendar
              </CardTitle>
              {user && (
                <Tabs value={calendarView} onValueChange={setCalendarView} className="mt-1.5">
                  <TabsList className="bg-muted/50 h-7">
                    <TabsTrigger value="all" className="text-[10px] h-5" data-testid="calendar-all-tab">All</TabsTrigger>
                    <TabsTrigger value="my" className="text-[10px] h-5" data-testid="calendar-my-tab">My Matches</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                onMonthChange={(d) => setSelectedDate(d)}
                className="rounded-md"
                modifiers={{ hasMatch: matchDates }}
                modifiersStyles={{
                  hasMatch: { backgroundColor: 'hsl(150, 60%, 45%)', color: 'hsl(0, 0%, 100%)', borderRadius: '50%' },
                }}
              />
              {matchesOnDate.length > 0 ? (
                <div className="mt-3 space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {selectedDateStr} ({matchesOnDate.length})
                  </p>
                  {matchesOnDate.map(m => (
                    <div key={m.id} className="p-2 rounded-lg bg-muted/20 border border-border" data-testid={`calendar-match-${m.id}`}>
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="flex-1 text-right truncate font-medium">{m.player1_name}</span>
                        <span className="text-muted-foreground">vs</span>
                        <span className="flex-1 truncate font-medium">{m.player2_name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-muted-foreground">
                        {m.scheduled_time && <span>{m.scheduled_time}</span>}
                        <span className="truncate">{m.tournament_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : selectedDate && (
                <p className="mt-3 text-[10px] text-muted-foreground text-center">No matches on {selectedDateStr}</p>
              )}
            </CardContent>
          </Card>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { to: "/rankings", icon: Crown, label: "Rankings", color: "text-secondary" },
              { to: "/players", icon: Users, label: "Members", color: "text-primary" },
              { to: "/tournaments", icon: Trophy, label: "Tournaments", color: "text-chart-3" },
              { to: "/live", icon: Lightning, label: "Match Centre", color: "text-chart-4" },
            ].map(link => (
              <Link key={link.to} to={link.to}>
                <Card className="hover:border-primary/30 transition-colors cursor-pointer">
                  <CardContent className="p-3 text-center">
                    <link.icon className={`w-5 h-5 ${link.color} mx-auto mb-1`} weight="fill" />
                    <p className="text-xs font-medium">{link.label}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}


function MyFinanceCard({ data, symbol }) {
  const fmt = (n) => `${symbol} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const unpaidFees = data.fees.filter(f => f.status === "unpaid");
  const pendingPayouts = data.payouts.filter(p => p.status === "pending");
  return (
    <Card className="border-secondary/30 bg-secondary/5" data-testid="my-finance-card">
      <CardContent className="p-4">
        <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
          <CurrencyCircleDollar className="w-4 h-4 text-secondary" weight="fill" />
          Your Finance Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <div className="bg-card border border-border rounded-lg p-2">
            <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Prizes Earned</p>
            <p className="text-base font-bold text-primary" data-testid="my-finance-prizes-earned">{fmt(data.total_prizes_earned)}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-2">
            <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Prizes Pending</p>
            <p className="text-base font-bold text-secondary" data-testid="my-finance-prizes-pending">{fmt(data.total_prizes_pending)}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-2">
            <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Fees Paid</p>
            <p className="text-base font-bold" data-testid="my-finance-fees-paid">{fmt(data.total_fees_paid)}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-2">
            <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Fees Owed</p>
            <p className="text-base font-bold text-destructive" data-testid="my-finance-fees-unpaid">{fmt(data.total_fees_unpaid)}</p>
          </div>
        </div>
        {(unpaidFees.length > 0 || pendingPayouts.length > 0) && (
          <div className="text-xs space-y-1 text-muted-foreground">
            {unpaidFees.length > 0 && (
              <p><span className="text-destructive font-medium">{unpaidFees.length}</span> unpaid fee{unpaidFees.length > 1 ? "s" : ""}: {unpaidFees.slice(0, 2).map(f => f.tournament_name).join(", ")}{unpaidFees.length > 2 ? "…" : ""}</p>
            )}
            {pendingPayouts.length > 0 && (
              <p><span className="text-secondary font-medium">{pendingPayouts.length}</span> pending payout{pendingPayouts.length > 1 ? "s" : ""} awaiting settlement</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
